import { memo } from 'react'
import { Handle, Position, type Node } from '@xyflow/react'
import { CircleStop, CheckCircle, XCircle, AlertTriangle, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export type EndType = 'success' | 'failure' | 'cancel' | 'default'
export type EndNodeStatus = 'idle' | 'reached'

export interface EndCanvasNodeData {
  label: string
  endType?: EndType
  status?: EndNodeStatus
  message?: string
  onDelete?: () => void
  [key: string]: unknown
}

export type EndCanvasNode = Node<EndCanvasNodeData, 'end'>

const endTypeColors: Record<EndType, { bar: string; bg: string; icon: string }> = {
  success: { bar: 'bg-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/40', icon: 'text-emerald-600 dark:text-emerald-400' },
  failure: { bar: 'bg-red-500', bg: 'bg-red-100 dark:bg-red-900/40', icon: 'text-red-600 dark:text-red-400' },
  cancel: { bar: 'bg-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/40', icon: 'text-amber-600 dark:text-amber-400' },
  default: { bar: 'bg-gray-500', bg: 'bg-gray-100 dark:bg-gray-900/40', icon: 'text-gray-600 dark:text-gray-400' },
}

const endTypeIcons: Record<EndType, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle,
  failure: XCircle,
  cancel: AlertTriangle,
  default: CircleStop,
}

interface EndCanvasNodeProps {
  data: EndCanvasNodeData
  selected?: boolean
}

function EndCanvasNodeComponent({ data, selected }: EndCanvasNodeProps) {
  const endType: EndType = data.endType || 'default'
  const Icon = endTypeIcons[endType]
  const colors = endTypeColors[endType]

  return (
    <div
      className={cn(
        'group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 min-w-[160px] max-w-[200px] overflow-hidden',
        'border border-gray-200 dark:border-gray-700',
        selected && 'ring-2 ring-gray-500 ring-offset-2 dark:ring-offset-gray-900 border-gray-500'
      )}
    >
      {/* Left color accent bar */}
      <div className={cn('absolute left-0 top-0 bottom-0 w-1', colors.bar)} />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
      />

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          data.onDelete?.()
        }}
        className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* Node Content */}
      <div className="pl-4 pr-3 py-3">
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 mt-0.5">
            <div className={cn('p-1.5 rounded-lg', colors.bg)}>
              <Icon className={cn('h-4 w-4', colors.icon)} />
            </div>
          </div>
          <div className="min-w-0 flex-1 pr-4">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label || 'End'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {data.message || (endType === 'success' ? 'Workflow completed' : endType === 'failure' ? 'Workflow failed' : 'End of workflow')}
            </p>
          </div>
        </div>
      </div>

      {/* No output handle - End node is terminal */}
    </div>
  )
}

export default memo(EndCanvasNodeComponent)
